import React, { useState } from 'react';
import { ArrowRight, Clock, Layers, FileCheck, Rocket, MessageSquare, Check, ChevronRight, CheckCircle2 } from 'lucide-react';
import { SITE_CONTACT } from '../data/siteData';

interface BookingCtaSectionProps {
  onOpenBooking: () => void;
}

export const BookingCtaSection: React.FC<BookingCtaSectionProps> = ({ onOpenBooking }) => {
  const [activeStep, setActiveStep] = useState<number>(1);

  const steps = [
    {
      step: 1,
      shortLabel: 'Diagnostic',
      title: '1. Diagnostic Brief',
      subtitle: '45-Min Technical Scope',
      desc: 'Submit your website, app, or system requirements. We inspect current bottlenecks, competitor gaps, and commercial objectives.',
      icon: Clock,
    },
    {
      step: 2,
      shortLabel: 'Architecture',
      title: '2. Architecture Audit',
      subtitle: 'Core Web Vitals & DB',
      desc: 'Our lead engineers map out sub-0.8s frontend architecture, M-Pesa STK payment rails, and database schema.',
      icon: Layers,
    },
    {
      step: 3,
      shortLabel: 'Quotation',
      title: '3. Fixed Quotation',
      subtitle: 'Lipa Pole Pole Ready',
      desc: 'Receive transparent KES pricing, guaranteed delivery timeline (48h–7d), and 3-part flexible Lipa Pole Pole installment plan.',
      icon: FileCheck,
    },
    {
      step: 4,
      shortLabel: 'Launch',
      title: '4. Production Launch',
      subtitle: 'Live Handover & SEO',
      desc: 'Deployment to high-speed LiteSpeed NVMe edge infrastructure with SSL, Google Maps 3-Pack, and post-launch maintenance.',
      icon: Rocket,
    },
  ];

  return (
    <section className="py-20 lg:py-24 bg-gradient-to-br from-[#080d11] via-[#0d1624] to-[#112337] text-white relative overflow-hidden text-left">
      <div className="absolute top-0 right-0 h-96 w-96 rounded-full bg-[#b88e4c]/10 blur-3xl pointer-events-none" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="rounded-3xl border border-slate-700/80 bg-slate-900/90 p-6 sm:p-12 text-center max-w-5xl mx-auto shadow-2xl backdrop-blur-xs">
          
          <div className="inline-flex items-center gap-2 rounded-full border border-[#009fe3]/40 bg-slate-900/80 px-3.5 py-1 text-xs font-semibold text-[#38bdf8] mb-4 font-mono">
            <Clock className="h-3.5 w-3.5 text-[#38bdf8]" />
            <span>Interactive Diagnostic &amp; Onboarding Roadmap</span>
          </div>

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white font-display">
            Book your strategy session.
          </h2>

          <p className="mt-4 text-base sm:text-lg text-slate-200 leading-relaxed font-normal max-w-2xl mx-auto">
            A 45-minute working session with our senior engineers. Walk away with a concrete architecture roadmap and quotation, not a sales pitch.
          </p>

          {/* =========================================================================
              VISUAL PROGRESS STEPPER COMPONENT
              ========================================================================= */}
          <div className="mt-10 mb-8 pt-4">
            <div className="text-xs font-mono font-bold uppercase tracking-wider text-[#c59b4c] mb-6 text-center">
              Our 4-Stage Transparent Engineering Stepper (Step {activeStep} of 4)
            </div>

            {/* Visual Connected Progress Bar */}
            <div className="max-w-2xl mx-auto mb-8 px-4 hidden sm:block">
              <div className="relative flex items-center justify-between">
                {/* Background Connecting Line */}
                <div className="absolute left-6 right-6 top-4 h-1 bg-slate-800 -z-0" />
                {/* Active Progress Connecting Line */}
                <div
                  className="absolute left-6 top-4 h-1 bg-[#c59b4c] transition-all duration-300 -z-0"
                  style={{ width: `${((activeStep - 1) / 3) * 100 * 0.85}%` }}
                />

                {steps.map((s) => {
                  const isCompleted = activeStep > s.step;
                  const isCurrent = activeStep === s.step;
                  return (
                    <button
                      key={s.step}
                      type="button"
                      onClick={() => setActiveStep(s.step)}
                      className="relative z-10 flex flex-col items-center group cursor-pointer focus:outline-hidden"
                      aria-label={`Go to Step ${s.step}: ${s.shortLabel}`}
                    >
                      <div
                        className={`h-8 w-8 rounded-full flex items-center justify-center font-mono text-xs font-bold transition-all duration-200 ${
                          isCompleted
                            ? 'bg-emerald-500 text-slate-950 ring-2 ring-emerald-400/40'
                            : isCurrent
                            ? 'bg-[#c59b4c] text-slate-950 ring-4 ring-[#c59b4c]/30 scale-110 shadow-lg'
                            : 'bg-slate-800 text-slate-400 group-hover:bg-slate-700'
                        }`}
                      >
                        {isCompleted ? <Check className="h-4 w-4 stroke-[3]" /> : s.step}
                      </div>
                      <span
                        className={`text-[11px] font-mono mt-1.5 transition-colors ${
                          isCurrent
                            ? 'text-[#c59b4c] font-bold'
                            : isCompleted
                            ? 'text-emerald-400'
                            : 'text-slate-400'
                        }`}
                      >
                        {s.shortLabel}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Stepper Interactive Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
              {steps.map((s) => {
                const isSelected = activeStep === s.step;
                const isCompleted = activeStep > s.step;
                const Icon = s.icon;
                return (
                  <div
                    key={s.step}
                    onClick={() => setActiveStep(s.step)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        setActiveStep(s.step);
                      }
                    }}
                    className={`rounded-2xl p-4 border transition-all cursor-pointer min-h-[140px] ${
                      isSelected
                        ? 'bg-slate-800/95 border-[#c59b4c] ring-2 ring-[#c59b4c]/40 shadow-xl'
                        : isCompleted
                        ? 'bg-slate-950/70 border-emerald-900/60 hover:bg-slate-900/80'
                        : 'bg-slate-950/60 border-slate-800 hover:border-slate-700 hover:bg-slate-900/60'
                    }`}
                  >
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span
                          className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-bold font-mono ${
                            isCompleted
                              ? 'bg-emerald-500 text-slate-950'
                              : isSelected
                              ? 'bg-[#c59b4c] text-slate-950 shadow-md'
                              : 'bg-slate-800 text-slate-300'
                          }`}
                        >
                          {isCompleted ? <Check className="h-3.5 w-3.5 stroke-[3]" /> : s.step}
                        </span>
                        <span className="text-xs font-bold text-white font-sans truncate">
                          {s.title}
                        </span>
                      </div>
                      <Icon className={`h-4 w-4 ${isSelected ? 'text-[#c59b4c]' : isCompleted ? 'text-emerald-400' : 'text-slate-400'}`} />
                    </div>

                    <div className="text-[11px] font-mono text-[#38bdf8] font-semibold mb-1">
                      {s.subtitle}
                    </div>

                    <p className="text-xs text-slate-200 leading-relaxed font-sans">
                      {s.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Action Buttons with Isolation Effect & Scarcity Signal (Rule 4 & Rule 8) */}
          <div className="mt-8 space-y-3">
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <button
                onClick={onOpenBooking}
                className="isolated-cta w-full sm:w-auto inline-flex items-center justify-center gap-2.5 rounded-xl bg-[#c59b4c] hover:bg-[#b08535] text-slate-950 font-bold px-9 py-4 text-sm transition-all duration-200 swiss-shadow-gold cursor-pointer min-h-[48px]"
              >
                <span className="phrase-keep">Book 45-Min Strategy Session</span>
                <ArrowRight className="h-4 w-4 stroke-[2.5] text-slate-950" />
              </button>

              <a
                href={SITE_CONTACT.whatsappUrl}
                target="_blank"
                rel="noreferrer"
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/90 hover:bg-slate-700 text-white font-semibold px-6 py-4 text-sm transition-colors cursor-pointer min-h-[48px]"
              >
                <MessageSquare className="h-4 w-4 text-emerald-400" />
                <span>Direct WhatsApp (+254 114 646 341)</span>
              </a>
            </div>

            {/* Scarcity Signal (Rule 4) */}
            <div className="flex items-center justify-center gap-2 text-xs font-mono text-slate-400 pt-1">
              <span className="text-[#c59b4c] font-semibold">✦ Strict Intake:</span>
              <span>Only 4 executive diagnostic sessions accepted per week · Current calendar 75% booked</span>
            </div>
          </div>

          <div className="mt-8 pt-8 border-t border-slate-800/80 grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs text-slate-200 font-sans">
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span>Full technology &amp; Core Web Vitals audit</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span>Clear KES pricing &amp; milestone schedule</span>
            </div>
            <div className="flex items-center justify-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />
              <span>Zero sales pressure guarantee</span>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};
